import React, {useCallback, useContext, useEffect, useState} from "react";
import moment from "moment";
import {Box, Button, ButtonBase, CircularProgress, InputBase, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
  CallIcon, InformationCircleIcon, PlugSocketIcon, SentIcon, Settings01Icon, WhatsappIcon
} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {SignInContext} from "../../../context/SignInContext";
import {useRegistryProject} from "../RegistryProjectContext";
import {Panel} from "../dashboard/components/Panel";
import {WhatsappConnectorRepository} from "../../../repositories/WhatsappConnectorRepository";
import {WhatsappConnectorDialog} from "./WhatsappConnectorDialog";

const DEFAULT_MESSAGE = "Hello! This is a test message from OpenBrain.";

const errorMessage = (error, fallback) => error?.response?.data?.message || error?.response?.data?.error || fallback;

// E.164: "+" and 8-15 digits; spaces, dashes, dots and brackets are dropped before sending
const normalizePhone = (phone) => phone.replace(/[\s().-]/g, "");
const isValidPhone = (phone) => /^\+?[1-9]\d{7,14}$/.test(normalizePhone(phone));

// WhatsApp's text length limit, checked by the backend too
const MAX_TEXT_LENGTH = 4096;

const senderOf = (status) => status?.displayPhoneNumber || status?.phoneNumberId || "";

const fieldSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 1.5,
  borderRadius: "12px",
  border: "1px solid #E4E4EE",
  bgcolor: "#fff",
  color: "#8A8AA0",
  "&:hover": {borderColor: "#D6D6E4"},
  "&:focus-within": {borderColor: "#8491F7", boxShadow: "0 0 0 3px rgba(132, 145, 247, 0.16)"},
};

const readOnlyFieldSx = {...fieldSx, height: 42, bgcolor: "#F4F4FA", "&:hover": {}};

const buttonSx = (dark) => ({
  height: 42, px: 2, borderRadius: "12px", textTransform: "none", fontSize: 14, fontWeight: 600,
  ...(dark
    ? {color: "#fff", bgcolor: "#23264D", "&:hover": {bgcolor: "#33376B"}, "&.Mui-disabled": {color: "#fff", bgcolor: "#C9CAD8"}}
    : {color: "#23264D", border: "1px solid #E4E4EE", bgcolor: "#fff", "&:hover": {bgcolor: "#F4F4FA"}}),
});

const Label = ({children, note}) => (
  <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} sx={{mb: 0.75, minHeight: 24}}>
    <Typography sx={{fontSize: 12, fontWeight: 500, color: "#6B6B80"}}>{children}</Typography>
    {note && <Typography sx={{fontSize: 11, color: "#8A8AA0"}}>{note}</Typography>}
  </Stack>
);

// Single-line field; readOnly shows it greyed out
const Field = ({label, note, value, onChange, placeholder, icon, readOnly, loading, error}) => (
  <Box>
    <Label note={note}>{label}</Label>
    <Box sx={readOnly ? readOnlyFieldSx : {...fieldSx, height: 42, ...(error && {borderColor: "#E58C8C"})}}>
      {loading ? <CircularProgress size={14} sx={{color: "#8491F7"}}/> : icon && <HugeiconsIcon icon={icon} size={16}/>}
      <InputBase
        value={loading ? "" : value}
        onChange={onChange && ((e) => onChange(e.target.value))}
        placeholder={loading ? "Loading…" : placeholder}
        disabled={readOnly}
        readOnly={readOnly}
        fullWidth
        inputProps={{"aria-label": label, autoComplete: "off"}}
        sx={{fontSize: 14, color: "#23264D", "& .Mui-disabled": {WebkitTextFillColor: "#4A4A63"}}}
      />
    </Box>
  </Box>
);

const TextArea = ({label, note, value, onChange, placeholder, minRows = 5, maxLength}) => (
  <Box>
    <Label note={note}>{label}</Label>
    <Box sx={{...fieldSx, alignItems: "flex-start", py: 1.25}}>
      <InputBase
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        multiline
        minRows={minRows}
        maxRows={14}
        fullWidth
        inputProps={{"aria-label": label, maxLength}}
        sx={{fontSize: 14, color: "#23264D", lineHeight: 1.6}}
      />
    </Box>
  </Box>
);

const Note = ({tone = "info", children}) => {
  const colors = tone === "warning"
    ? {bg: "#FDF8EE", border: "#F5E6C8", icon: "#A86A0C", text: "#7A5A1E"}
    : {bg: "#EEF0FE", border: "#DDE1FB", icon: "#4B55C9", text: "#4A4A63"};
  return (
    <Stack direction={"row"} gap={1} sx={{p: 1.5, borderRadius: "12px", bgcolor: colors.bg, border: `1px solid ${colors.border}`}}>
      <Box sx={{color: colors.icon, display: "flex", pt: 0.25}}><HugeiconsIcon icon={InformationCircleIcon} size={16}/></Box>
      <Typography component={"div"} sx={{fontSize: 13, color: colors.text}}>{children}</Typography>
    </Stack>
  );
};

// Connected number or not connected, and the button that connects / edits it
const ConnectionStatus = ({status, loading, onOpen}) => {
  const connected = Boolean(status?.connected);
  return (
    <Stack direction={"row"} alignItems={"center"} gap={1.25} flexWrap={"wrap"}>
      {!loading && status && (
        <Box sx={{
          display: "inline-flex", alignItems: "center", gap: 0.75, height: 32, px: 1.5, borderRadius: "999px",
          fontSize: 13, fontWeight: 600,
          color: connected ? "#1E8A55" : "#A86A0C",
          bgcolor: connected ? "#E8F7EF" : "#FDF4E3",
        }}>
          <Box component={"span"} sx={{width: 7, height: 7, borderRadius: "50%", bgcolor: connected ? "#22C55E" : "#E0A232"}}/>
          {connected ? `Connected · ${status.connectorName}` : "Not connected"}
        </Box>
      )}
      <Button
        onClick={onOpen}
        disabled={loading}
        startIcon={<HugeiconsIcon icon={connected ? Settings01Icon : PlugSocketIcon} size={16}/>}
        sx={buttonSx(false)}
      >
        {connected ? "WhatsApp settings" : "Connect WhatsApp"}
      </Button>
    </Stack>
  );
};

// Right side: the message as the recipient sees it in WhatsApp
const WhatsappPreview = ({sender, to, message}) => (
  <Panel sx={{overflow: "hidden", minHeight: 560}}>
    <Stack direction={"row"} alignItems={"center"} gap={1} sx={{px: 3, py: 2, borderBottom: "1px solid #F0F0F6"}}>
      <HugeiconsIcon icon={WhatsappIcon} size={18} color={"#8491F7"}/>
      <Typography component={"h2"} sx={{fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>
        Preview
      </Typography>
      <Typography sx={{ml: "auto", fontSize: 12, color: "#8A8AA0"}} noWrap>to {to || "—"}</Typography>
    </Stack>

    {/* chat header of the recipient's phone */}
    <Stack direction={"row"} alignItems={"center"} gap={1.5} sx={{px: 2.5, py: 1.5, bgcolor: "#075E54", color: "#fff"}}>
      <Box sx={{
        width: 36, height: 36, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center",
        justifyContent: "center", bgcolor: "rgba(255, 255, 255, 0.18)",
      }}>
        <HugeiconsIcon icon={WhatsappIcon} size={18}/>
      </Box>
      <Box sx={{minWidth: 0}}>
        <Typography noWrap sx={{fontSize: 14, fontWeight: 600}}>{sender || "Your business"}</Typography>
        <Typography sx={{fontSize: 11, opacity: 0.75}}>Business account</Typography>
      </Box>
    </Stack>

    <Box sx={{flex: 1, minHeight: 460, p: 2.5, bgcolor: "#ECE5DD", display: "flex", flexDirection: "column", alignItems: "flex-end"}}>
      <Box sx={{
        maxWidth: "85%", px: 1.5, pt: 1, pb: 0.5, bgcolor: "#DCF8C6", borderRadius: "10px 0 10px 10px",
        boxShadow: "0 1px 1px rgba(0, 0, 0, 0.08)",
      }}>
        <Typography sx={{fontSize: 14, color: "#111B21", whiteSpace: "pre-wrap", wordBreak: "break-word"}}>
          {message || <Box component={"span"} sx={{color: "#8A8AA0"}}>(empty message)</Box>}
        </Typography>
        <Typography sx={{fontSize: 11, color: "#667781", textAlign: "right", mt: 0.25}}>
          {moment().format("HH:mm")} <Box component={"span"} sx={{color: "#53BDEB"}}>✓✓</Box>
        </Typography>
      </Box>
    </Box>
  </Panel>
);

export const RegistryWhatsappConnectorPage = () => {
  const {project} = useRegistryProject();
  const {currentUser} = useContext(SignInContext);
  const projectId = project?.id;

  const [status, setStatus] = useState(null);
  const [connectors, setConnectors] = useState([]);
  const [statusLoading, setStatusLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [to, setTo] = useState(currentUser?.phoneNumber || currentUser?.phone_number || "");
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [sending, setSending] = useState(false);

  const loadConnection = useCallback(() => {
    if (!projectId) return;
    setStatusLoading(true);
    Promise.all([WhatsappConnectorRepository.getStatus(projectId), WhatsappConnectorRepository.getByProject(projectId)])
      .then(([statusResponse, connectorsResponse]) => {
        setStatus(statusResponse.data);
        setConnectors(connectorsResponse.data || []);
      })
      .catch(error => toast.error(errorMessage(error, "Failed to load the WhatsApp connection")))
      .finally(() => setStatusLoading(false));
  }, [projectId]);

  useEffect(loadConnection, [loadConnection]);

  const connected = Boolean(status?.connected);
  const connector = connectors.find(item => item.id === status?.connectorId);
  const phoneValid = isValidPhone(to);
  const contentValid = Boolean(message.trim()) && message.length <= MAX_TEXT_LENGTH;

  const sendTest = () => {
    const phone = normalizePhone(to);
    setSending(true);
    WhatsappConnectorRepository.send(projectId, {to: phone, text: message})
      .then(({data}) => toast.success(`Test message sent to +${data?.to || phone.replace(/^\+/, "")}`))
      .catch(error => toast.error(errorMessage(error, error?.response?.status === 409
        ? "Connect an enabled WhatsApp number first"
        : "Failed to send the test message")))
      .finally(() => setSending(false));
  };

  // the connected number, else one that is disabled, else a new one
  const editConnectorId = status?.connectorId || connectors[0]?.id || null;
  const closeDialog = useCallback(() => setDialogOpen(false), []);

  return (
    <Box sx={{flex: 1, minHeight: 0, bgcolor: "#F7F7FB", overflowY: "auto"}}>
      <Stack gap={3} sx={{maxWidth: 1280, mx: "auto", width: "100%", p: {xs: 2, md: 4}, pb: "100px", boxSizing: "border-box"}}>
        <Stack direction={{xs: "column", md: "row"}} justifyContent={"space-between"} alignItems={{md: "flex-end"}} gap={2}>
          <Box>
            <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7"}}>CONNECTORS</Typography>
            <Typography component={"h1"} sx={{fontSize: 26, fontWeight: 700, color: "#23264D", letterSpacing: "-0.02em"}}>
              WhatsApp Connector
            </Typography>
            <Typography sx={{fontSize: 14, color: "#6B6B80"}}>
              Send WhatsApp messages from your business number and see how they look
            </Typography>
          </Box>
          <ConnectionStatus status={status} loading={statusLoading} onOpen={() => setDialogOpen(true)}/>
        </Stack>

        <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", lg: "minmax(0, 1fr) minmax(0, 1fr)"}, gap: 2, alignItems: "start"}}>
          <Panel sx={{p: 3, gap: 2.5}}>
            <Box>
              <Typography component={"h2"} sx={{fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>
                Test message
              </Typography>
              <Typography sx={{mt: 0.25, fontSize: 13, color: "#8A8AA0"}}>
                Send a message to your own phone to check the connection
              </Typography>
            </Box>

            {!statusLoading && !connected && (
              <Note tone={"warning"}>
                No WhatsApp number is connected.{" "}
                <ButtonBase onClick={() => setDialogOpen(true)} sx={{fontSize: 13, fontWeight: 600, color: "#4B55C9", verticalAlign: "baseline"}}>
                  Connect WhatsApp
                </ButtonBase>{" "}
                to send messages.
              </Note>
            )}

            <Field label={"From"} value={senderOf(status)} placeholder={"—"} icon={CallIcon} readOnly loading={statusLoading}/>
            <Field label={"To"} note={"International format, e.g. +38970123456"} value={to} onChange={setTo}
                   placeholder={"+38970123456"} icon={CallIcon} error={Boolean(to) && !phoneValid}/>

            <TextArea label={"Message"} note={`${message.length} / ${MAX_TEXT_LENGTH}`} value={message} onChange={setMessage}
                      placeholder={"Write your message…"} maxLength={MAX_TEXT_LENGTH}/>
            <Note>
              WhatsApp only delivers the message if this number messaged your business in the last 24 hours. Send
              any message from your phone to the business number first.
            </Note>

            <Stack direction={"row"} justifyContent={"flex-end"} sx={{pt: 0.5}}>
              <Button
                onClick={sendTest}
                disabled={sending || !connected || !phoneValid || !contentValid}
                startIcon={sending
                  ? <CircularProgress size={14} sx={{color: "#fff"}}/>
                  : <HugeiconsIcon icon={SentIcon} size={16}/>}
                sx={buttonSx(true)}
              >
                {sending ? "Sending…" : "Send test message"}
              </Button>
            </Stack>
          </Panel>

          <Box sx={{position: {lg: "sticky"}, top: {lg: 0}}}>
            <WhatsappPreview sender={connector?.verifiedName || status?.displayPhoneNumber || status?.connectorName}
                             to={normalizePhone(to)} message={message}/>
          </Box>
        </Box>
      </Stack>

      <WhatsappConnectorDialog
        open={dialogOpen}
        projectId={projectId}
        connectorId={editConnectorId}
        onClose={closeDialog}
        onSaved={loadConnection}
      />
    </Box>
  );
};
